import { forwardRef } from "react";

import { TreeSelect } from "antd";
import {
  type RefTreeSelectProps,
  type TreeSelectProps,
} from "antd/lib/tree-select";

import { IconCaretSmallDown } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import "./BuiTreeSelect.less";

/**
 * A single node in a BuiTreeSelect's `treeData`.
 *
 * Structurally compatible with the app's `BTSelectItem` so existing folder-tree
 * data can be passed straight through. Only the fields the tree picker actually
 * renders are declared here; extra properties on the source objects are ignored.
 */
export interface IBuiTreeSelectNode {
  /** Stable React key for the node. */
  key: string | number;
  /** The value committed to the form when this node is selected. */
  value: string | number;
  /** Display label for the node. */
  title: React.ReactNode;
  /** When true, the node cannot be selected. */
  disabled?: boolean;
  /** Optional leading icon rendered before the title. */
  icon?: React.ReactNode;
  /** Additional class name applied to the tree node. */
  className?: string;
  /**
   * Text used when matching this node against the search input. Falls back to
   * `title` when omitted.
   */
  searchValue?: string;
  /** Child nodes, rendered as an expandable branch. */
  children?: IBuiTreeSelectNode[];
}

/**
 * Props for {@link BuiTreeSelect}.
 *
 * Aliases antd's single-select `TreeSelectProps` and narrows `treeData` to
 * {@link IBuiTreeSelectNode}. Declared as its own type so BuiTreeSelect-specific
 * props can be added or narrowed without breaking consumers.
 */
export interface IBuiTreeSelectProps
  extends Omit<
    TreeSelectProps<string | number>,
    "treeData" | "multiple" | "value"
  > {
  "data-testid"?: string;
  treeData?: IBuiTreeSelectNode[] | undefined;
  /** Selected value; `undefined` clears the selection. */
  value?: string | number | undefined;
  /**
   * When true, search only matches leaf/child nodes; a parent is kept only when
   * a descendant matches. When false, a parent's own label is matched too.
   * @default true
   */
  onlySearchChildNodes?: boolean;
}

const { TreeNode } = TreeSelect;

/** Height of 8.5 tree nodes, matching the legacy folder-picker dropdown height. */
const STANDARD_OPTION_HEIGHT = 32;
const TREE_POPUP_HEIGHT = 8.5 * STANDARD_OPTION_HEIGHT;

function nodeMatchesSearch(node: IBuiTreeSelectNode, search: string): boolean {
  if (node.disabled) {
    return false;
  }
  // The `title?.toString()` fallback only yields a usable haystack for string
  // titles. `title` is typed as ReactNode, so a JSX title (e.g. icon + label)
  // stringifies to "[object Object]" and won't match — set `searchValue`
  // explicitly on any node whose title isn't a plain string.
  const haystack = (
    node.searchValue ??
    node.title?.toString() ??
    ""
  ).toLowerCase();
  return haystack.includes(search.toLowerCase());
}

/**
 * Decides whether antd should treat a single node as a search match. antd calls
 * this for every node and shows the whole subtree of any node that returns true,
 * while automatically keeping the ancestors of a matched node visible.
 *
 * A parent whose only relevance is a matching descendant therefore returns
 * `false` (antd still keeps it as the matched leaf's ancestor); returning `true`
 * would wrongly reveal the parent's unrelated siblings. A parent returns `true`
 * only when `onlySearchChildNodes` is false and its own label matches, which is
 * the intentional "match the parent and reveal its children" case. Mirrors the
 * legacy `BTSelect` tree filter so the folder pickers behave identically.
 */
function filterNode(
  node: IBuiTreeSelectNode,
  search: string,
  onlySearchChildNodes: boolean,
): boolean {
  if (!onlySearchChildNodes && nodeMatchesSearch(node, search)) {
    return true;
  }

  if (node.children && node.children.length > 0) {
    const childMatch = node.children.some((child) =>
      child.children && child.children.length > 0
        ? filterNode(child, search, onlySearchChildNodes)
        : nodeMatchesSearch(child, search),
    );
    if (!childMatch) {
      return onlySearchChildNodes ? false : nodeMatchesSearch(node, search);
    }
    return false;
  }

  return nodeMatchesSearch(node, search);
}

function renderTreeNode(node: IBuiTreeSelectNode) {
  return (
    <TreeNode
      key={node.key}
      value={node.value}
      title={node.title}
      disabled={node.disabled}
      icon={node.icon}
      className={node.className}
      isLeaf={!node.children || node.children.length === 0}
    >
      {node.children?.map(renderTreeNode)}
    </TreeNode>
  );
}

/**
 * BuiTreeSelect - a styled wrapper around antd's single-select `TreeSelect`.
 *
 * Renders hierarchical `treeData` with expand/collapse, node icons, and a
 * caret suffix, applying the `bui-tree-select` class for BDS-themed styling and
 * forwarding a ref to the underlying antd TreeSelect. It owns presentation and
 * search; Formik/business logic belongs in the core `BTTreeSelect` wrapper.
 *
 * @param props - {@link IBuiTreeSelectProps} defining the tree's data and behavior.
 * @param ref - forwarded ref to the underlying antd TreeSelect instance.
 * @example
 * <BuiTreeSelect treeData={folders} value={folderId} onChange={handleChange} />
 */
export const BuiTreeSelect = forwardRef<
  RefTreeSelectProps,
  IBuiTreeSelectProps
>(
  (
    {
      treeData,
      className,
      dropdownClassName,
      onlySearchChildNodes = true,
      showSearch = true,
      treeDefaultExpandAll = true,
      suffixIcon,
      dropdownRender,
      value,
      "data-testid": testid,
      ...props
    },
    ref,
  ) => {
    // Only pass the `value` key when it is defined. antd/rc-select treats the
    // mere presence of the `value` prop as "controlled", so spreading
    // `value={undefined}` would override an uncontrolled selection; omitting the
    // key entirely lets antd manage it. Mirrors the legacy BTSelect workaround.
    const valueObject = value === undefined ? {} : { value };

    // Wrap the popup in a `${testid}-popup` container so tests can scope option
    // queries to the open dropdown, matching the legacy BTSelect tree path.
    const handleDropdownRender = (menu: React.ReactElement) => (
      <div data-testid={testid ? `${testid}-popup` : undefined}>
        {dropdownRender ? dropdownRender(menu) : menu}
      </div>
    );

    return (
      <TreeSelect<string | number>
        {...props}
        {...valueObject}
        ref={ref}
        data-testid={testid}
        className={cn("bui-tree-select", className)}
        dropdownClassName={cn("bui-tree-select-dropdown", dropdownClassName)}
        showSearch={showSearch}
        treeDefaultExpandAll={treeDefaultExpandAll}
        treeIcon
        listHeight={TREE_POPUP_HEIGHT}
        dropdownRender={handleDropdownRender}
        suffixIcon={
          suffixIcon ?? (
            <IconCaretSmallDown
              testid="bui-tree-select-caret"
              style={{ pointerEvents: "none" }}
            />
          )
        }
        filterTreeNode={(search: string, node) =>
          filterNode(
            node as unknown as IBuiTreeSelectNode,
            search,
            onlySearchChildNodes,
          )
        }
      >
        {treeData?.map(renderTreeNode)}
      </TreeSelect>
    );
  },
);

BuiTreeSelect.displayName = "BuiTreeSelect";
